'use client';

import { API_ROUTES, useApiInvalidate, useApiMutation } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useEffect, useRef } from 'react';
import { PENDING_FAVOURITE_STORAGE_KEY } from './useFavourites';

type PendingFavouriteData = { name: string; favouritesRoute: string };

type UsePendingFavouriteReplayOptions = {
  onSuccess?: (data: PendingFavouriteData) => void;
  onError?: (data: PendingFavouriteData) => void;
};

function parsePendingFavourite(raw: string): { id: string } & PendingFavouriteData {
  try {
    const parsed = JSON.parse(raw) as { id: string; name?: string; favouritesRoute?: string };
    return { id: parsed.id, name: parsed.name ?? '', favouritesRoute: parsed.favouritesRoute ?? '' };
  } catch {
    return { id: raw, name: '', favouritesRoute: '' };
  }
}

function usePendingFavouriteReplay({ onSuccess, onError }: UsePendingFavouriteReplayOptions = {}) {
  const { isAuthenticated } = useSession();
  const { invalidateQuery } = useApiInvalidate();
  const hasReplayed = useRef(false);
  const { mutate } = useApiMutation<void, { game: string; position: number }>(API_ROUTES.PLAYER_DATA.FAVOURITES_GAMES, 'POST', { requestOptions: { credentials: true } });

  useEffect(() => {
    if (!isAuthenticated || hasReplayed.current) return;
    const raw = sessionStorage.getItem(PENDING_FAVOURITE_STORAGE_KEY);
    if (!raw) return;
    hasReplayed.current = true;

    const { id, name, favouritesRoute } = parsePendingFavourite(raw);

    mutate(
      { game: id, position: 0 },
      {
        onSuccess: () => {
          sessionStorage.removeItem(PENDING_FAVOURITE_STORAGE_KEY);
          invalidateQuery(API_ROUTES.PLAYER_DATA.FAVOURITES_GAMES);
          onSuccess?.({ name, favouritesRoute });
        },
        onError: () => {
          onError?.({ name, favouritesRoute });
        },
      }
    );
  }, [isAuthenticated, mutate, invalidateQuery, onSuccess, onError]);
}

export { usePendingFavouriteReplay };
